import { Button, DatePicker, Modal, Select, Table, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useState } from 'react'
import { deleteZbMilestoneApi, updateZbMilestoneApi } from '@/services/apis/milestoneZb'
import { formatZbPercent, zbErrorMessage } from './data'

interface EditProps {
  values: API.MilestoneZb.Item[]
  projectID: string | number
  // Viewers (milestoneView without milestoneEdit/opAdmin) can still edit
  // 计划日期/实际完成日期/状态 and save them; readOnly only gates 删除.
  readOnly?: boolean
  onUpdate: () => void
  onCancel: () => void
}

type Draft = API.MilestoneZb.Item & { dirty?: boolean }

const Edit: React.FC<EditProps> = ({ values, projectID, readOnly = false, onUpdate, onCancel }) => {
  const [list, setList] = useState<Draft[]>(() => values.map(item => ({ ...item })))
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [modal, modalHolder] = Modal.useModal()
  const busy = saving || deleting

  const update = (id: number, patch: Partial<Draft>) => {
    setList(current => current.map(item => item.id === id ? { ...item, ...patch, dirty: true } : item))
  }

  const save = async () => {
    if (busy) return
    // 计划日期与状态暂不做任何条件限制：计划日期可留空/清空，状态可在
    // 未完成/已完成之间自由切换，已完成也不再要求实际完成日期。
    // 日期以空字符串发送表示清空（后端据此置 null），而非省略字段不变更。
    setSaving(true)
    try {
      for (const item of list.filter(value => value.dirty)) {
        const saved = await updateZbMilestoneApi(projectID, item.id, {
          planDate: item.planDate ?? '',
          actualDate: item.actualDate ?? '',
          status: item.status,
        })
        // A previous successful row must not be submitted twice if a later
        // row fails.
        setList(current => current.map(row => row.id === item.id
          ? { ...saved, dirty: false } : row))
      }
      message.success('保存成功')
      onUpdate()
    } catch (error) {
      message.error(zbErrorMessage(error, '保存失败'))
    } finally {
      setSaving(false)
    }
  }

  const remove = (item: Draft) => {
    modal.confirm({
      title: '确认删除该里程碑节点？删除后不可恢复。',
      onOk: async () => {
        setDeleting(true)
        try {
          await deleteZbMilestoneApi(projectID, item.id)
          setList(current => current.filter(value => value.id !== item.id))
          message.success('删除成功')
        } catch (error) {
          message.error(zbErrorMessage(error, '删除失败'))
        } finally {
          setDeleting(false)
        }
      },
    })
  }

  const columns: ColumnsType<Draft> = [
    { title: '节点名称', dataIndex: 'name' },
    {
      // 全部开放编辑：不分权限/状态，任意日期均可，也可清空（保存时置空计划日期）。
      title: '计划日期',
      render: (_, item) => <DatePicker
        disabled={busy}
        value={item.planDate ? dayjs(item.planDate) : null}
        onChange={date => update(item.id, { planDate: date ? date.format('YYYY-MM-DD') : null })}
      />,
    },
    {
      // 与计划日期一样全部开放编辑：不分权限/状态均可填写或清空，
      // 不因 readOnly 或未完成状态而禁用。
      title: '实际完成日期',
      render: (_, item) => <DatePicker
        disabled={busy}
        maxDate={dayjs()}
        value={item.actualDate ? dayjs(item.actualDate) : null}
        onChange={date => update(item.id, { actualDate: date ? date.format('YYYY-MM-DD') : null })}
      />,
    },
    {
      // 全部开放编辑：不分权限/状态直接切换，不再弹出完成时间选择、不要求
      // 实际完成日期，已完成节点也可改回未完成或继续编辑。
      title: '状态',
      render: (_, item) => <Select
        disabled={busy}
        value={item.status}
        options={[{ value: 0, label: '未完成' }, { value: 1, label: '已完成' }]}
        onChange={status => update(item.id, status === 0 ? { status, actualDate: null } : { status })}
      />,
    },
    { title: '进度占比', render: (_, item) => formatZbPercent(item.percentage) },
    {
      title: '操作',
      render: (_, item) => readOnly ? null : <Button danger type="link" disabled={busy} onClick={() => remove(item)}>删除</Button>,
    },
  ]

  return <div>
    {modalHolder}
    <Table<Draft> rowKey="id" dataSource={list} pagination={false} scroll={{ x: 720 }} columns={columns} />
    <div className="flex justify-end gap-2 mt-4">
      <Button disabled={busy} onClick={onCancel}>取消</Button>
      {/* 保存对所有人开放，否则计划日期/状态改了也提交不了。 */}
      <Button type="primary" disabled={deleting} loading={saving} onClick={() => void save()}>保存</Button>
    </div>
  </div>
}

export default Edit
